import { Col, Row } from 'antd'
import Input from 'components/Input'
import { InputFieldTypes } from 'components/Input/types'
import useFrequenciesFilter from '../context/hook'
import { FrequenciesFilterField, FrequencyHealthStatus, SignalLevelFilter } from '../lib/types'
import { RfTuningLimitValues } from '../status/redux/types'

type FrequencyFiltersProps = {
  frequencyLimits: Array<RfTuningLimitValues> 
}

function getAvailableModulations(frequencyLimits: Array<RfTuningLimitValues> ) {
  const emptyModulation = {value: '', label: 'rf_tuning_filter.modulations_all'}
  const availableModulations = frequencyLimits.map(item => item.modulation)
    .filter((value, index, array) => array.indexOf(value) === index)
    .map(item => ({ label: item, value: item }))

  return [emptyModulation, ...availableModulations]
}

function getSignalLevelFilterOptions() {
  return [
    {
      label: '',
      value: '',
    },
    {
      label: 'rf_tuning_filter.signal_level_filter_equal_or_less_than',
      value: SignalLevelFilter.equalOrLessThan
    },
    {
      label: 'rf_tuning_filter.signal_level_filter_greater_than',
      value: SignalLevelFilter.greaterThan
    },
  ]
}

function getSignalHealthFilterOptions() {
  return [
    {
      label: 'rf_tuning_filter.signal_health_all',
      value: '',
    },
    {
      label: 'rf_tuning_filter.signal_health_healthy',
      value: FrequencyHealthStatus.healthy,
    },
    {
      label: 'rf_tuning_filter.signal_health_unhealthy',
      value: FrequencyHealthStatus.unhealthy,
    },
  ]
}

export default function FrequencyFilters({ frequencyLimits }: FrequencyFiltersProps) {
  const { filter, setFilter } = useFrequenciesFilter()

  return <div className="frequencies-filters-container">
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12} lg={8} xxl={4}>
        <Input
          type={InputFieldTypes.text} 
          value={filter[FrequenciesFilterField.TSID]} 
          setValue={(value) => {
            setFilter({ fieldName: FrequenciesFilterField.TSID, value })
          }}
          labelParams={{ 
            i18nTag: 'rf_tuning_filter.tsid',
            onClean: () => setFilter({ fieldName: FrequenciesFilterField.TSID, value: '' }), 
          }}
        />
      </Col>
      <Col xs={24} sm={12} lg={8} xxl={4}>
        <Input
          type={InputFieldTypes.text} 
          value={filter[FrequenciesFilterField.frequency]} 
          setValue={(value) => {
            setFilter({ fieldName: FrequenciesFilterField.frequency, value })
          }}
          labelParams={{ 
            i18nTag: 'rf_tuning_filter.frequency',
            onClean: () => setFilter({ fieldName: FrequenciesFilterField.frequency, value: '' }), 
          }}
        />
      </Col>
      <Col xs={24} sm={12} lg={8} xxl={4}>
        <Input
          type={InputFieldTypes.select}
          value={filter[FrequenciesFilterField.modulation]}
          setValue={(value) => {
            setFilter({ fieldName: FrequenciesFilterField.modulation, value })
          }}
          options={getAvailableModulations(frequencyLimits)}
          labelParams={{
            i18nTag: 'rf_tuning_filter.modulations',
            onClean: () => setFilter({ fieldName: FrequenciesFilterField.modulation, value: '' }), 
          }}
        />
      </Col>
       <Col xs={24} sm={12} lg={8} xxl={4}>
        <Input
          type={InputFieldTypes.select}
          value={filter[FrequenciesFilterField.signalHealth]}
          setValue={(value) => {
            const castValue = value as FrequencyHealthStatus
            setFilter({ fieldName: FrequenciesFilterField.signalHealth, value: castValue })
          }}
          options={getSignalHealthFilterOptions()}
          labelParams={{
            i18nTag: 'rf_tuning_filter.signal_health',
            onClean: () => setFilter({ fieldName: FrequenciesFilterField.signalHealth, value: '' }),
          }}
        />
      </Col>
      <Col xs={24} sm={12} lg={8} xxl={4}>
        <Input
          type={InputFieldTypes.select}
          value={filter[FrequenciesFilterField.signalLevel].filter}
          setValue={(filterName) => {
            const filterNameCast = filterName as SignalLevelFilter
            setFilter({ fieldName: FrequenciesFilterField.signalLevel, filter: filterNameCast, value: filter[FrequenciesFilterField.signalLevel].value })
          }}
          options={getSignalLevelFilterOptions()} 
          labelParams={{
            i18nTag: 'rf_tuning_filter.signal_level_filter',
            onClean: () => {
              setFilter({
                fieldName: FrequenciesFilterField.signalLevel,
                filter: '',
                value: 0,
              })
            }, 
          }}
        />
      </Col>
      <Col xs={24} sm={12} lg={8} xxl={4}>
        <Input
          type={InputFieldTypes.text}
          value={filter[FrequenciesFilterField.signalLevel].value ? filter[FrequenciesFilterField.signalLevel].value.toString() : ''}
          setValue={(value) => {
            setFilter({ fieldName: FrequenciesFilterField.signalLevel, filter: filter[FrequenciesFilterField.signalLevel].filter, value: parseFloat(value) })
          }}
          disabled={!filter[FrequenciesFilterField.signalLevel].filter }
          labelParams={{
            i18nTag: 'rf_tuning_filter.signal_level_value',
            onClean: () => {
              setFilter({
                fieldName: FrequenciesFilterField.signalLevel,
                filter: filter[FrequenciesFilterField.signalLevel].filter,
                value: 0,
              })
            }, 
          }}
        />
      </Col>
    </Row>
  </div>
}
